import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  SvgIcon,
  Checkbox,
  Tooltip,
} from "@mui/material";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import { Add, Remove, ExpandMore } from "@mui/icons-material";
import "./CartItemCard.scss";
import { ReactComponent as MinusIcon } from "../../../assets/icons/minus-icon.svg";
import { ReactComponent as TrashIcon } from "../../../assets/icons/trash-icon.svg";
import { useNavigate } from "react-router-dom";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import DeleteServicePopup from "./DeleteServicePopup";
import { ReactComponent as NoImgIcon } from "../../../assets/icons/no-img-icon.svg";

type CartItemCardProps = {
  item: any;
  onDeleteService?: (serviceId: number) => void;
  onDeleteSubService?: (subServiceId: number) => void;
  isSelected?: boolean;
  onSelect?: () => void;
  showCheckbox?: boolean;

  //  NEW: disable checkbox when another is selected
  isDisabled?: boolean;
};

const CartItemCard: React.FC<CartItemCardProps> = ({
  item,
  onDeleteService,
  onDeleteSubService,
  isSelected,
  onSelect,
  showCheckbox,
  isDisabled,
}) => {
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [confirmAction, setConfirmAction] = useState<() => void>(() => { });
  const navigate = useNavigate();

  const isPlanner = !!item?.isPlannerPackage;

  const handleServiceDeleteClick = (serviceId: number) => {
    //  planner delete confirmation message
    setPopupMessage(
      isPlanner
        ? "Are you sure you want to delete this package?"
        : "Are you sure you want to delete this service and all its sub-services?",
    );

    setConfirmAction(() => () => {
      onDeleteService?.(serviceId);
    });

    setPopupOpen(true);
  };

  const handleSubServiceDeleteClick = (cartItemId: number) => {
    setPopupMessage("Are you sure you want to delete this sub-service?");

    setConfirmAction(() => () => {
      onDeleteSubService?.(cartItemId);
    });

    setPopupOpen(true);
  };

  //  image pick should prefer actual images (API sometimes includes documents too)
  const partnerImage = useMemo(() => {
    const images = Array.isArray(item?.partnerImages) ? item.partnerImages : [];
    const firstImage =
      images.find((x: any) => x?.mediaType === "image" && x?.url) ||
      images.find((x: any) => x?.url); // fallback
    return firstImage?.url ?? "";
  }, [item]);

  const handlePartnerClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    //  planner navigates to planners details
    if (isPlanner) {
      const plannerId = item?.plannerId;
      if (plannerId)
        navigate(`/home/list-of-funeral-homes/preneed-planners/${plannerId}`);
      return;
    }

    // existing provider behavior
    navigate(`/home/list-of-funeral-homes/details/${item.partnerId}`);
  };

  return (
    <Box className="cart-item-card__container">
      {showCheckbox && (
        <Box
          className="cart-item-card__checkbox-outer"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={!!isSelected}
            onChange={onSelect}
            disabled={!!isDisabled}
            sx={{
              color: "#104145",
              "&.Mui-checked": {
                color: "#104145",
              },
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          />
        </Box>
      )}

      <Box className="cart-item-card__wrapper">
        {/* ======================================================
             PLANNER CARD (matches screenshot)
           ====================================================== */}
        {isPlanner ? (
          <>
            <Box
              className={`cart-item-card ${isSelected ? "cart-item-card--selected" : ""
                }`}
              onClick={showCheckbox ? onSelect : undefined}
              sx={{
                cursor: showCheckbox ? "pointer" : "default",
                position: "relative",
              }}
            >
              {/* Left: Image */}
              <Box className="cart-item-card__image">
                {partnerImage ? (
                  <img src={partnerImage} alt={item?.partnerName} />
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      height: "140px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "1rem",
                      borderRadius: "6px 6px 0 0",
                      color: "#555",
                      backgroundColor: "#F9FAFC",
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    <SvgIcon
                      component={NoImgIcon}
                      style={{ height: "48px", width: "48px" }}
                      inheritViewBox
                    />
                    <Typography>No Image Available</Typography>
                  </Box>
                )}

                <Box className="cart-item-card__rating">
                  <StarRoundedIcon className="cart-item-card__rating-icon" />
                  <Typography className="cart-item-card__rating-value">
                    {item.partnerAvgRating || "4.0"}
                  </Typography>
                </Box>
              </Box>

              {/* Middle: Planner Info */}
              <Box className="cart-item-card__info" sx={{ pr: 1 }}>
                <Box
                  className="cart-item-card__info-header"
                  sx={{ alignItems: "center", gap: "6px" }}
                >
                  <Typography
                    className="cart-item-card__partner-name"
                    onClick={handlePartnerClick}
                    sx={{
                      maxWidth: "100%",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {item.partnerName}
                  </Typography>

                  {item.partnerLocation ? (
                    <>
                      <FiberManualRecordIcon
                        sx={{ color: "#104145", margin: 0, width: 10 }}
                      />
                      <Typography
                        className="cart-item-card__partner-location"
                        sx={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {item.partnerLocation}
                      </Typography>
                    </>
                  ) : null}

                  {/*  FIX: planner delete works (cartItemId is in item.partnerId) */}
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleServiceDeleteClick(item.partnerId);
                    }}
                    sx={{ marginLeft: "auto" }}
                  >
                    <TrashIcon />
                  </IconButton>
                </Box>

                {/* description */}
                {item.partnerDescription ? (
                  <Tooltip
                    title={item.partnerDescription}
                    arrow
                    placement="top"
                  >
                    <Typography className="cart-item-card__partner-desc">
                      {item.partnerDescription}
                    </Typography>
                  </Tooltip>
                ) : null}

                {/*  Package block (as screenshot) */}
                <Box sx={{ mt: 1.5 }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 18,
                      color: "#000",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {item.packageName}
                    {item.needType && (
                      <>
                        <Box
                          component="span"
                          sx={{
                            color: "#104145",
                            mx: 1,
                            fontWeight: 900,
                            fontSize: "18px",
                            lineHeight: 1,
                          }}
                        >
                          &bull;
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            color: "#666",
                            fontSize: "14px",
                            fontWeight: 400,
                          }}
                        >
                          {item.needType === "pre_need" ? "Preneed" : "Immediate Need"}
                        </Box>
                      </>
                    )}
                  </Typography>
                  {item.packageDescription ? (
                    <Typography
                      sx={{ mt: 0.5, color: "#104145", fontSize: 14 }}
                    >
                      {item.packageDescription}
                    </Typography>
                  ) : null}

                  {/* Services Included */}
                  <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #E9AE12" }}>
                    <Typography sx={{ fontWeight: 700, mb: 1, fontSize: 16 }}>
                      Services Included
                    </Typography>

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                      {(item.packageServices ?? []).map(
                        (s: any, idx: number) => (
                          <Box
                            key={`${s?.serviceName ?? "svc"}-${idx}`}
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <span style={{ color: "#104145", fontWeight: 700 }}>
                              ✔
                            </span>
                            <Typography
                              sx={{
                                fontSize: 14,
                                fontWeight: 500,
                                color: "#000",
                              }}
                            >
                              {s?.serviceName}
                              {s?.needType && (
                                <>
                                  <Box
                                    component="span"
                                    sx={{
                                      color: "#104145",
                                      mx: 1,
                                      fontWeight: 900,
                                      fontSize: "18px",
                                      lineHeight: 1,
                                    }}
                                  >
                                    &bull;
                                  </Box>
                                  <Box
                                    component="span"
                                    sx={{
                                      color: "#666",
                                      fontSize: "14px",
                                      fontWeight: 400,
                                    }}
                                  >
                                    {s?.needType === "pre_need"
                                      ? "Preneed"
                                      : "Immediate Need"}
                                  </Box>
                                </>
                              )}
                            </Typography>
                          </Box>
                        ),
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Total footer */}
            <Box className="cart-item-card__price-summary">
              <Typography className="cart-item-card__price-summary-label">
                Total Price
              </Typography>
              <Typography className="cart-item-card__price-summary-value">
                ${" "}
                {Number(
                  item.packagePrice ?? item.partnerTotalPrice ?? 0,
                ).toFixed(0)}
              </Typography>
            </Box>
          </>
        ) : (
          <>
            {/* ======================================================
                EXISTING PROVIDER CARD (updated)
               ====================================================== */}
            <Box
              className={`cart-item-card ${isSelected ? "cart-item-card--selected" : ""
                }`}
              onClick={showCheckbox ? onSelect : undefined}
              sx={{ cursor: showCheckbox ? "pointer" : "default" }}
            >
              {/* Left: Image */}
              <Box className="cart-item-card__image">
                {partnerImage ? (
                  <img src={partnerImage} alt={item?.partnerName} />
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "0.5rem",
                      color: "#555",
                      backgroundColor: "#F2F5F9",
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    <SvgIcon
                      component={NoImgIcon}
                      sx={{ height: "40px", width: "40px", color: "#A0AEC0" }}
                      inheritViewBox
                    />
                    <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#4A5568" }}>
                      No Image Available
                    </Typography>
                  </Box>
                )}
                <Box className="cart-item-card__rating">
                  <StarRoundedIcon className="cart-item-card__rating-icon" />
                  <Typography className="cart-item-card__rating-value">
                    {item.partnerAvgRating || "4.0"}
                  </Typography>
                </Box>
              </Box>

              {/* Middle: Partner Info */}
              <Box className="cart-item-card__info">
                <Box className="cart-item-card__info-header">
                  <Typography
                    className="cart-item-card__partner-name"
                    onClick={(e) => handlePartnerClick(e)}
                  >
                    {item.partnerName}
                  </Typography>

                  <Box
                    component="span"
                    sx={{
                      color: "#104145",
                      mx: 0.5,
                      fontWeight: 900,
                      fontSize: "18px",
                      lineHeight: 1,
                    }}
                  >
                    &bull;
                  </Box>

                  <Typography className="cart-item-card__partner-location">
                    {item.partnerLocation}
                  </Typography>
                </Box>
                {/* No description shown in provider cart card as per image */}
              </Box>
            </Box>

            {/* ======= ACCORDION SECTION (updated) ======= */}
            <Box className="cart-item-card__accordion-section">
              {item.services.map((service: any) => (
                <Accordion
                  key={service.serviceId}
                  className="cart-item-card__accordion"
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    className="cart-item-card__accordion-summary"
                  >
                    <Typography
                      className="cart-item-card__service-name"
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      {service.serviceName}
                      {service.needType && (
                        <>
                          <Box
                            component="span"
                            sx={{
                              color: "#104145",
                              mx: 1,
                              fontWeight: 900,
                              fontSize: "18px",
                              lineHeight: 1,
                            }}
                          >
                            &bull;
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              color: "#666",
                              fontSize: "14px",
                              fontWeight: 400,
                            }}
                          >
                            {service.needType === "pre_need"
                              ? "Preneed"
                              : "Immediate Need"}
                          </Box>
                        </>
                      )}
                    </Typography>

                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleServiceDeleteClick(service.serviceId);
                      }}
                      style={{ marginLeft: "auto" }}
                    >
                      <TrashIcon />
                    </IconButton>
                  </AccordionSummary>

                  <AccordionDetails className="cart-item-card__accordion-details">
                    <Typography className="cart-item-card__accordion-summary-title">
                      Summary of Steps -{" "}
                      <span style={{ color: "#9D3535", fontWeight: 400 }}>
                        Customized.
                      </span>
                    </Typography>

                    {service.subServices.map((sub: any) => (
                      <Box
                        key={`${service.serviceId}-${sub.subServiceId}`}
                        className="cart-item-card__subservice"
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            handleSubServiceDeleteClick(sub.cartItemId)
                          }
                        >
                          <MinusIcon />
                          <Typography className="cart-item-card__subservice-name">
                            {sub.subServiceName}
                          </Typography>
                        </Box>

                        <Box className="cart-item-card__subservice-actions">
                          {sub.itemType === "merchandise" &&
                            sub.quantity > 0 && (
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 700,
                                  color: "#104145",
                                  mr: 2,
                                }}
                              >
                                Quantity: {sub.quantity}
                              </Typography>
                            )}
                          <Typography className="cart-item-card__subservice-price">
                            $ {sub.price}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            <Box className="cart-item-card__price-summary">
              <Typography className="cart-item-card__price-summary-label">
                Total Price
              </Typography>

              <Typography className="cart-item-card__price-summary-value">
                $ {item.partnerTotalPrice}
              </Typography>
            </Box>
          </>
        )}
      </Box>

      <DeleteServicePopup
        open={popupOpen}
        message={popupMessage}
        onClose={() => setPopupOpen(false)}
        onConfirm={() => {
          confirmAction();
          setPopupOpen(false);
        }}
      />
    </Box>
  );
};

export default CartItemCard;
